import { useState, useEffect, useCallback, useRef } from 'react'
import {
  DocumentService,
  type VentureDocument,
} from '../../../../services/documents/documentService'
import { useAuthContext } from '../../../../context/auth'

const POLL_INTERVAL_MS = 3000
const MAX_POLL_COUNT = 60 // Stop polling after ~3 minutes

/**
 * Hook for managing venture document state: upload, list, delete, and status polling.
 */
export function useVentureDocuments(ventureNodeId: string, mindmapId: string) {
  const { user } = useAuthContext()
  const [documents, setDocuments] = useState<VentureDocument[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollCountRef = useRef(0)
  const unmountedRef = useRef(false)

  // Track unmount to prevent state updates after cleanup
  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
    }
  }, [])

  const fetchDocuments = useCallback(async () => {
    if (!mindmapId || !ventureNodeId || !user?.id) return
    try {
      const docs = await DocumentService.listDocuments(mindmapId, ventureNodeId)
      if (!unmountedRef.current) {
        setDocuments(docs)
        setIsLoaded(true)
      }
    } catch (err) {
      if (!unmountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load documents')
        setIsLoaded(true)
      }
    }
  }, [mindmapId, ventureNodeId, user?.id])

  useEffect(() => {
    void fetchDocuments()
  }, [fetchDocuments])

  // Poll while any document is processing (with timeout)
  useEffect(() => {
    const hasProcessing = documents.some((d) => d.status === 'processing')

    if (hasProcessing && pollCountRef.current < MAX_POLL_COUNT) {
      pollRef.current ??= setInterval(() => {
        pollCountRef.current++
        if (pollCountRef.current >= MAX_POLL_COUNT) {
          if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
          }
          return
        }
        void fetchDocuments()
      }, POLL_INTERVAL_MS)
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      // Reset poll count when all documents finish processing
      if (!hasProcessing) {
        pollCountRef.current = 0
      }
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [documents, fetchDocuments])

  const uploadDocument = useCallback(
    async (file: File, contentClassification: string) => {
      if (!user?.id || !mindmapId || !ventureNodeId) return

      setIsUploading(true)

      try {
        await DocumentService.uploadDocument(
          file,
          ventureNodeId,
          mindmapId,
          user.id,
          contentClassification
        )
        await fetchDocuments()
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed'
        if (!unmountedRef.current) {
          setError((prev) => (prev ? `${prev}\n${file.name}: ${msg}` : `${file.name}: ${msg}`))
        }
      } finally {
        if (!unmountedRef.current) {
          setIsUploading(false)
        }
      }
    },
    [user?.id, mindmapId, ventureNodeId, fetchDocuments]
  )

  const deleteDocument = useCallback(async (documentId: string) => {
    setError(null)
    try {
      await DocumentService.deleteDocument(documentId)
      if (!unmountedRef.current) {
        setDocuments((prev) => prev.filter((d) => d.id !== documentId))
      }
    } catch (err) {
      if (!unmountedRef.current) {
        setError(err instanceof Error ? err.message : 'Delete failed')
      }
    }
  }, [])

  return {
    documents,
    isUploading,
    isLoaded,
    error,
    setError,
    uploadDocument,
    deleteDocument,
  }
}
